import * as React from 'react';
import { useSelector } from 'react-redux';
import { browserHistory } from 'react-router';

import { get } from 'lodash';

import { selectAppReady, selectCurrentUser } from '@lib/selectors';
import storage from '@lib/services/storageService';

import { STORAGE_KEYS } from 'utils/constants';
import { isPatientLoginOnly } from 'utils/isPatientLoginOnly';

const loginRequired = (currentUser: { $isWidgetUser?: boolean } | null, pathname: string) => {
  const isAuthenticated = storage.get(STORAGE_KEYS.authentication) && !isPatientLoginOnly();

  if (isAuthenticated) {
    return false;
  }
  // Routes that needs authentication independent of has or not a currentUser
  if (isPatientLoginOnly() && !/login/.test(pathname)) {
    return true;
  }

  // app is not ready or invalid token/user
  if (!currentUser) {
    return false;
  }

  // Routes that requires authentication for Widget Users (url that have the "user=" query param)
  if (currentUser.$isWidgetUser) {
    return /main/.test(pathname);
  }
};

const getRedirectToLoginPage = (currentUser: unknown) => () => {
  const returnUrl = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
  const firstName = get(currentUser, 'firstname', '');
  const lastName = get(currentUser, 'lastname');
  const dateOfBirth = get(currentUser, 'dateOfBirth');
  const date = `${get(dateOfBirth, 'year')}-${get(dateOfBirth, 'month')}-${get(dateOfBirth, 'day')}`;
  if (isPatientLoginOnly()) {
    const query = new URLSearchParams(window.location.search);
    query.set('returnUrl', `${window.location.pathname}${window.location.search}`);
    const newUrl = new URL(`/login?${query.toString()}`, window.location.href);
    browserHistory.push(`${newUrl.pathname}${newUrl.search}`);
  } else {
    browserHistory.push(
      `/login?firstname=${firstName}&lastname=${lastName}&dateOfBirth=${date}&returnUrl=${returnUrl}`,
    );
  }
};

const useRequireAuthentication = (pathname: string) => {
  const appReady = useSelector(selectAppReady);
  const currentUser = useSelector(selectCurrentUser);

  const needsAuthentication = React.useMemo(() => appReady && loginRequired(currentUser, pathname), [
    appReady,
    currentUser,
    pathname,
  ]);
  const redirectToLoginPage = React.useMemo(() => getRedirectToLoginPage(currentUser), [currentUser]);

  // remove the authentication from storage when the tab is closed/refresh
  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    const removeStorage = () => {
      storage.remove(STORAGE_KEYS.authentication);
    };
    // eslint-disable-next-line no-restricted-globals
    addEventListener('beforeunload', removeStorage);
  }, []);

  const values = React.useMemo(
    () => ({
      needsAuthentication,
      redirectToLoginPage,
    }),
    [needsAuthentication, redirectToLoginPage],
  );

  return values;
};

export default useRequireAuthentication;
